import React, { forwardRef, type ReactNode } from "react";
import { Field } from "@base-ui/react/field";
import { Fieldset } from "@base-ui/react/fieldset";
import { RadioGroup } from "@base-ui/react/radio-group";
import type { BaseProps } from "@purpur/common-types";
import { Heading, type HeadingTagType } from "@purpur/heading";
import c from "classnames/bind";

import type { RADIO_BUTTON_GROUP_ORIENTATION, RadioButtonGroupDirection } from "./constants";
import { RadioButtonGroupContext } from "./radio-button-group.context";
import styles from "./radio-button-group.module.scss";
import { RadioButtonGroupItem } from "./radio-button-group-item";

const cx = c.bind(styles);

export type RadioButtonGroupRootProps = Omit<BaseProps, "id"> & {
  /**
   * ID of the radio group container.
   * */
  id: string;
  /**
   * To use when no heading is given (do not combine with `aria-labelledby`).
   * */
  ["aria-label"]?: string;
  /**
   * To use if no heading is provided (do not combine with `aria-label`). The id of the element labeling this radio group.
   * */
  ["aria-labelledby"]?: string;
  /**
   * The value of the radio item that should be checked when initially rendered. Use when you do not need to control the state of the radio items.
   * */
  defaultValue?: string;
  /**
   * The reading direction of the radio group. If omitted, it assumes LTR (left-to-right) reading mode.
   * */
  dir?: RadioButtonGroupDirection;
  /**
   * When true, prevents the user from interacting with radio items.
   * */
  disabled?: boolean;
  /**
   * When true, applies negative styling to the radio group.
   */
  negative?: boolean;
  /**
   * The radio items.
   *
      RadioButtonGroupItem = {
        ["data-testid"]?: string;
        disabled?: boolean;
        helperText?: string;
        id: string;
        label: string;
        ref?: Ref<HTMLSpanElement>;
        value: string;
      }[]
   * */
  items?: RadioButtonGroupItem[];
  /**
   * Radio group children. Best used with `RadioButtonGroup.Item`.
   */
  children?: ReactNode;
  /**
   * The name of the group. Submitted with its owning form as part of a name/value pair.
   * */
  name?: string;
  /**
   * Event handler called when the value changes.
   * */
  onValueChange?: (value: string) => void;
  /**
   * The orientation of the component.
   * */
  orientation?: (typeof RADIO_BUTTON_GROUP_ORIENTATION)[number];
  /**
   * When true, indicates that the user must check a radio item before the owning form can be submitted.
   * */
  required?: boolean;
  /**
   * The controlled value of the radio item to check. Should be used in conjunction with onValueChange.
   * */
  value?: string;
  /**
   * Renders above the radio group as a heading.
   * */
  heading?: string;
  /**
   * Changes heading level for heading.
   */
  headingTag?: HeadingTagType;
};

const rootClassName = "purpur-radio-button-group";

export const RadioButtonGroupRoot = forwardRef<HTMLDivElement, RadioButtonGroupRootProps>(
  (
    {
      "data-testid": dataTestId,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledby,
      className,
      items,
      heading,
      orientation = "vertical",
      headingTag = "h2",
      children,
      name,
      disabled,
      negative,
      dir,
      required,
      ...props
    },
    ref
  ) => {
    if (!items?.length && !children) {
      return null;
    }

    const ariaProps = heading ? {} : { "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby };

    return (
      <RadioButtonGroupContext.Provider value={{ disabled, negative, dir }}>
        <Field.Root className={cx(`${rootClassName}__container`)} name={name} disabled={disabled}>
          <Fieldset.Root
            render={
              <RadioGroup
                {...props}
                className={cx(className, rootClassName)}
                required={required}
                {...ariaProps}
                data-orientation={orientation}
                data-testid={dataTestId}
                dir={dir}
                ref={ref}
              />
            }
          >
            {heading && (
              <Fieldset.Legend
                render={
                  <Heading
                    tag={headingTag}
                    data-testid={dataTestId ? `${dataTestId}-heading` : undefined}
                    variant="subsection-100"
                    negative={negative}
                  >
                    {required && <span aria-hidden>{"* "}</span>}
                    {heading}
                  </Heading>
                }
              />
            )}
            <div className={cx(`${rootClassName}__items-container`)}>
              {items?.length
                ? items.map((item) => <RadioButtonGroupItem key={item.value} {...item} />)
                : children}
            </div>
          </Fieldset.Root>
        </Field.Root>
      </RadioButtonGroupContext.Provider>
    );
  }
);

RadioButtonGroupRoot.displayName = "RadioButtonGroupRoot";
